import React, { useState, useMemo } from 'react';
import { FaDownload } from 'react-icons/fa6';
import { MinioService } from '@/api/minio';
import {
  FaRegFilePdf,
  FaRegFileWord,
  FaRegFileExcel,
  FaFileCsv,
  FaRegFileAlt,
  FaRegFileArchive,
  FaFileVideo,
  FaFileImage,
  FaRegFilePowerpoint,
  FaFileCode,
} from 'react-icons/fa';
import { downloadFile } from '@/utils/libs/fileUtil';
import { IoIosClose } from 'react-icons/io';
import { env } from '@/utils/configs/env';
import { MdRemoveRedEye } from 'react-icons/md';

interface Media {
  id: string;
  name: string;
  path: string;
  size: string;
  created_at: string;
  updated_at: string;
}

interface MediaItemProps {
  media: Media;
}

export const MediaItem: React.FC<MediaItemProps> = ({ media }) => {
  const { mutate } = MinioService.useDownloadFile();
  const [isOpenImage, setIsOpenImage] = useState<boolean>(false);

  const previewUrl = useMemo(
    () => `${env.apiBaseUrl}/download/${media.path}`,
    [media.path],
  );

  const downloadAction = () => {
    mutate(previewUrl, {
      onSuccess: data => downloadFile(data, media.name),
      onError: () => console.error('Failed to download file'),
    });
  };

  const showMedia = () => setIsOpenImage(true);

  const getFileIcon = (fileName: string) => {
    if (fileName.endsWith('.pdf'))
      return <FaRegFilePdf size={50} color="red" />;
    if (fileName.endsWith('.doc') || fileName.endsWith('.docx'))
      return <FaRegFileWord size={50} color="blue" />;
    if (fileName.endsWith('.xls') || fileName.endsWith('.xlsx'))
      return <FaRegFileExcel size={50} color="green" />;
    if (fileName.endsWith('.csv')) return <FaFileCsv size={50} color="green" />;
    if (
      fileName.endsWith('.txt') ||
      fileName.endsWith('.log') ||
      fileName.endsWith('.ipynb')
    )
      return <FaRegFileAlt size={50} color="orange" />;
    if (fileName.endsWith('.zip'))
      return <FaRegFileArchive size={50} color="orange" />;
    if (fileName.endsWith('.ppt') || fileName.endsWith('.pptx'))
      return <FaRegFilePowerpoint size={50} color="orangered" />;
    if (
      fileName.endsWith('.gif') ||
      fileName.endsWith('.png') ||
      fileName.endsWith('.jpg')
    )
      return <FaFileImage size={50} color="purple" />;
    if (
      fileName.endsWith('.mp4') ||
      fileName.endsWith('.webm') ||
      fileName.endsWith('.mov')
    )
      return <FaFileVideo size={50} color="black" />;
    return <FaFileCode size={50} color="gray" />;
  };

  const renderMediaPreview = (fileName: string, previewUrl: string) => {
    if (fileName.match(/\.(mp4|webm|mov)$/i)) {
      return (
        <video width="100" height="100" style={{ marginBottom: '20px' }} controls>
          <source src={previewUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      );
    }
    return <div>{getFileIcon(fileName)}</div>;
  };

  const renderFullMediaPreview = (fileName: string, previewUrl: string) => {
    if (fileName.match(/\.(mp4|webm|mov)$/i)) {
      return (
        <video width="100%" height="auto" controls>
          <source src={previewUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      );
    }
    return (
      <div
        style={{
          padding: '20px',
          textAlign: 'center',
          color: 'white',
          fontSize: '30px',
        }}
      >
        <p>Preview not available for this file type</p>
      </div>
    );
  };

  return (
    <div className="flex justify-center">
      <div className="mr-[10px]">
        {media.name.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
          <img
            src={previewUrl}
            alt={media.name}
            className="w-[150px] h-[100px] object-cover"
          />
        ) : (
          renderMediaPreview(media.name, previewUrl)
        )}
      </div>

      <div className="flex flex-row gap-2">
        <button type="button" className="text-gray-500" onClick={showMedia}>
          <MdRemoveRedEye size={17} />
        </button>

        <button type="button" onClick={downloadAction}>
          <FaDownload />
        </button>
      </div>

      {isOpenImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-x-hidden overflow-y-auto bg-black/80">
          <div className="relative flex flex-col items-center justify-center w-full max-h-screen p-4 max-w-7xl">
            <div className="relative flex justify-center w-full">
              {media.name.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                <img
                  src={previewUrl}
                  className="object-contain max-w-full max-h-screen"
                  alt={media.name}
                />
              ) : (
                renderFullMediaPreview(media.name, previewUrl)
              )}
              <button
                className="fixed z-50 p-1 text-2xl transition-all delay-100 bg-white rounded-full hover:bg-lime-200 top-2 right-2"
                onClick={() => setIsOpenImage(false)}
              >
                <IoIosClose />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
